<template>
  <div>
    <loading-indicator v-if="isLoading"/>

    <login-form v-else-if="requiresLogin" @logged-in="onLoggedIn" />

    <form-container v-else-if="errorMsg" size="medium">
      <b-alert variant="danger" show>{{ errorMsg }}</b-alert>
    </form-container>

    <form-container v-else-if="hasUser || requiresInitialPassword" size="medium">
      <template v-if="requiresInitialPassword">
        <h1 class="page-title">{{ $t('onboarding.signupTitle') }}</h1>
        <h4 class="subtitle">{{ $t('onboarding.signupDescription') }}</h4>
      </template>
      <template v-else>
        <h1 class="page-title">{{ $t('onboarding.invitationTitle') }}</h1>
      </template>

      <div v-if="requiresTermsOfServiceConsent" class="option">
        <p class="option__text">
          {{ $t('onboarding.terms') }}
          <a :href="$t('onboarding.termsUrl')">{{ $t('onboarding.termsLink') }}</a>
        </p>

        <div class="option__agree">
          <b-form-checkbox v-model="didAcceptTermsOfService">
            <span>{{ $t('onboarding.acceptTermsOfService') }}</span>
          </b-form-checkbox>
        </div>
      </div>

      <div v-if="requiresInitialPassword" class="option">
        <p class="option__text">
          {{ $t('onboarding.credentials', { username }) }}
        </p>

        <b-form-group horizontal
                      :label-cols="3"
                      label-size="sm"
                      label-class="text-sm-right"
                      :label="$t('onboarding.password')"
                      label-for="password1">
          <b-form-input id="password1"
                        name="password1"
                        type="password"
                        v-model="password1"/>
        </b-form-group>

        <b-alert v-if="password1 && !isPassword1Valid" variant="info" show>{{ $t('onboarding.enterPasswordMsg') }}</b-alert>

        <b-form-group horizontal
                      v-if="isPassword1Valid"
                      :label-cols="3"
                      label-size="sm"
                      label-class="text-sm-right"
                      :label="$t('onboarding.passwordRepeated')"
                      label-for="password2">
          <b-form-input id="password2"
                        name="password2"
                        type="password"
                        v-model="password2"/>
        </b-form-group>

        <b-alert v-if="isPassword1Valid && !passwordsMatch" variant="info" show>{{ $t('onboarding.repeatPasswordMsg') }}</b-alert>

        <b-alert v-if="isPassword1Valid && passwordsMatch" variant="success" show>{{ $t('onboarding.passwordsMatchMsg') }}</b-alert>
      </div>

      <div v-if="requiresInvitationConsent" class="option">
        <p class="option__text">
          {{ $t('onboarding.invitation', { organizationName: organizationName }) }}
        </p>

        <div class="option__agree">
          <b-form-checkbox v-model="didAcceptInvitation">
            <span>{{ $t('onboarding.acceptInvitation') }}</span>
          </b-form-checkbox>
        </div>
      </div>

      <div class="buttons">
        <b-button
            @click="commit"
            v-bind:disabled="!isCommitButtonEnabled"
            variant="primary">
          {{ $t('onboarding.commit') }}
        </b-button>
      </div>
    </form-container>
  </div>
</template>

<script>
import Parse from 'parse'
import { mapGetters } from 'vuex'
import LoginForm from '@/components/LoginForm'

/**
* This screen is used to handle onboarding of users:
* - signing up new users
* - accepting organization invitations
* - giving consent to usage terms
*
* Accepts an invitation token via the URL query parameter `t`, e.g. #invite?token=xyz
* This parameter is required when signing up a new user (since the user cannot yet log in).
* For all other cases, a logged in user is required.
*/
export default {
  name: 'OnboardingScreen',
  components: { 'login-form': LoginForm },
  data () {
    return {
      password1: '',
      password2: '',
      didAcceptTermsOfService: false,
      didAcceptInvitation: false,
      onboarding: {},
      isLoading: false,
      error: null
    }
  },
  async mounted () {
    this.fetch()
  },
  computed: {
    ...mapGetters([
      'hasUser'
    ]),
    membershipId () {
      return this.$route.query.t
    },
    requiresTermsOfServiceConsent () {
      return this.onboarding.requiresTermsOfServiceConsent
    },
    requiresInvitationConsent () {
      // the owner created the organization, so no need to invite
      return !!this.onboarding.organizationName && this.onboarding.role !== 'owner'
    },
    isAdmin () {
      return ['admin', 'owner'].includes(this.onboarding.role)
    },
    organizationName () {
      return this.onboarding.organizationName
    },
    requiresInitialPassword () {
      return this.onboarding.requiresInitialPassword
    },
    username () {
      return this.onboarding.username
    },
    isCommitButtonEnabled () {
      return (!this.requiresTermsOfServiceConsent || this.didAcceptTermsOfService) &&
             (!this.requiresInvitationConsent || this.didAcceptInvitation) &&
             (!this.requiresInitialPassword || (this.isPassword1Valid && this.passwordsMatch))
    },
    isPassword1Valid () {
      return this.password1.length >= 7
    },
    passwordsMatch () {
      return this.password1 === this.password2
    },
    errorMsg () {
      if (!this.error) {
        return
      }

      if (!this.error.message) {
        return this.$t('common.unexpectedError')
      }
      switch (this.error.message) {
        case 'invitation_expired': return this.$t('onboarding.invitationExpiredError')
        case 'invitation_already_accepted': return this.$t('onboarding.invitationAlreadyAcceptedError')
        case 'invitation_not_found': return this.$t('onboarding.invitationNotFoundError')
        case 'requires_login': return this.$t('onboarding.requiresLoginError')
        case 'invalid_user': return this.$t('onboarding.invalidUserError')
        default: return this.$t('common.unexpectedErrorWithDetails', { error: JSON.stringify(this.error.message) })
      }
    },
    requiresLogin () {
      // - if no token is provided, a user must be logged in to perform any onboarding action
      // - if a call to checkOnboarding was performed, the server might return with an error indicating that a logged-in user is required
      return (!this.membershipId && !this.hasUser) || (this.error && this.error.message === 'requires_login')
    }
  },
  methods: {
    async fetch () {
      this.isLoading = true
      this.error = null

      try {
        this.onboarding = await Parse.Cloud.run('checkOnboarding', { membershipId: this.membershipId })
        if (this.onboarding.language) {
          this.$store.commit('setUILanguage', this.onboarding.language)
        }
      } catch (error) {
        this.error = error
      }

      this.isLoading = false
    },
    async commit () {
      this.isLoading = true
      this.error = null

      try {
        await Parse.Cloud.run('commitOnboarding', {
          membershipId: this.membershipId,
          initialPassword: this.password1,
          termsOfServiceConsentGiven: this.didAcceptTermsOfService,
          invitationAccepted: this.didAcceptInvitation
        })

        if (this.requiresInitialPassword) {
          const params = { email: this.onboarding.username, password: this.password1 }
          await this.$store.dispatch('login', params)
        }

        this.$router.push({ name: this.isAdmin ? 'onboarding-success-admin' : 'onboarding-success' })
      } catch (error) {
        this.error = error
      }

      this.isLoading = false
    },
    async onLoggedIn () {
      this.fetch()
    }
  }
}
</script>

<style scoped>
.subtitle {
  font-size: 1.2em;
  margin-top: 40pt;
}

.option {
  display: block;
  margin-top: 30pt;
  padding: 15pt;
  background-color: rgba(170, 170, 170, 0.2);
  border-radius: 10pt;
}

.option__text {
  font-size: 1.2em;
}

.option__agree {
  margin-top: 20pt;
  background-color: rgba(170, 170, 170, 0.2);
  padding: 5pt;
  font-size: 1.2em;
}

.buttons {
  margin-top: 15pt;
}
</style>



// WEBPACK FOOTER //
// src/components/OnboardingScreen.vue